import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import CheckIcon from '@material-design-icons/svg/filled/check.svg?react';
import StarIcon from '@material-design-icons/svg/filled/star.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';

import { S3Image } from '../../../components/S3Image';
import { Button } from '../../../components/ui/button';
import type { Candidate } from '../types';

interface CandidateCardProps {
  candidate: Candidate;
  isAdded: boolean;
  canAdd: boolean;
  onAdd: () => void;
}

const ANALYTICS_TODO_RISK_NOTE =
  'TODO: join install exposure, impressions, and usage from the anonymized analytics feed before Claude ranking.';

export function CandidateCard({
  candidate,
  isAdded,
  canAdd,
  onAdd,
}: CandidateCardProps) {
  const rating = candidate.averageReviewScore;
  const reviewCount = candidate.reviewCount;
  const installs = candidate.totalInstalls;
  const riskNotes =
    candidate.riskNotes?.filter((note) => note !== ANALYTICS_TODO_RISK_NOTE) ??
    [];

  return (
    <div className="flex flex-col rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
      <div className="flex items-start gap-3">
        <S3Image
          src={candidate.iconUrl}
          alt=""
          aria-hidden="true"
          className="size-10 rounded-lg object-cover ring-1 ring-white/10"
          fallback={
            <div className="flex size-10 items-center justify-center rounded-lg bg-neutral-800/60 ring-1 ring-white/10">
              <AppsIcon className="size-5 fill-current text-neutral-500" />
            </div>
          }
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white">
            {candidate.dappName}
          </div>
          {candidate.subtitle && (
            <div className="truncate text-xs text-neutral-400">
              {candidate.subtitle}
            </div>
          )}
          <div className="truncate font-mono text-[11px] text-neutral-600">
            {candidate.androidPackage}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-400">
        {candidate.storePrimaryCategory && (
          <span className="rounded-full border border-neutral-800 bg-neutral-950/50 px-2 py-0.5 text-[11px] text-neutral-300">
            {candidate.storePrimaryCategory}
          </span>
        )}
        {typeof rating === 'number' && (
          <span className="flex items-center gap-1">
            <StarIcon className="size-3.5 fill-current text-amber-400" />
            <span className="text-neutral-200">{rating.toFixed(1)}</span>
            {typeof reviewCount === 'number' && (
              <span className="text-neutral-500">
                ({reviewCount.toLocaleString()})
              </span>
            )}
          </span>
        )}
        {typeof installs === 'number' && (
          <span>{installs.toLocaleString()} installs</span>
        )}
      </div>

      {candidate.reasons.length > 0 && (
        <ul className="mt-2 space-y-1">
          {candidate.reasons.slice(0, 3).map((reason) => (
            <li
              key={reason}
              className="flex items-start gap-1.5 text-xs text-neutral-300"
            >
              <CheckIcon className="mt-0.5 size-3 shrink-0 fill-current text-teal-400" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      )}

      {riskNotes.length > 0 && (
        <ul className="mt-2 space-y-1">
          {riskNotes.slice(0, 2).map((note) => (
            <li
              key={note}
              className="flex items-start gap-1.5 text-xs text-amber-300/90"
            >
              <WarningIcon className="mt-0.5 size-3 shrink-0 fill-current" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex justify-end">
        {isAdded ? (
          <span className="flex items-center gap-1 text-xs font-medium text-teal-400">
            <CheckIcon className="size-3.5 fill-current" />
            Added
          </span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={!canAdd}
            onClick={onAdd}
          >
            <AddIcon className="size-4 fill-current" />
            Add
          </Button>
        )}
      </div>
    </div>
  );
}
